import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = "Fly'N Out";
const SITE_URL = 'https://flynout.com';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85';
const DEFAULT_DESCRIPTION = 'Where every flight becomes a community. Log flights, share route experiences, get real-time airport updates, and connect with fellow travelers worldwide.';
const DEFAULT_KEYWORDS = 'flight community, route experiences, airport tips, travel community, seat tips, flight reviews, layover buddy';
export default function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  keywords = DEFAULT_KEYWORDS,
  noIndex = false,
  structuredData,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Where every flight becomes a community`;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      'https://twitter.com/flynout',
      'https://instagram.com/flynout',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@thesaudigate.com',
      contactType: 'customer service',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/discover?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Mobile / PWA */}
      <meta name="theme-color" content="#FF4500" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@flynout" />
      <meta name="twitter:creator" content="@flynout" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
}

// Helper for airport pages
export function AirportSEO({ airport }) {
  if (!airport) return null;
  const title = `${airport.name} (${airport.iata_code}) — Live Feed & Guide`;
  const description = `Explore ${airport.name} (${airport.iata_code}) in ${airport.city}, ${airport.country}. Live traveler posts, crowd levels, lounge reviews, and transit tips from the Fly N Out community.`;
  const keywords = `${airport.iata_code} airport, ${airport.city} airport, ${airport.name}, airport tips, airport community, layover ${airport.city}, airport chat ${airport.city}`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Airport',
    name: airport.name,
    iataCode: airport.iata_code,
    icaoCode: airport.icao_code,
    address: {
      '@type': 'PostalAddress',
      addressLocality: airport.city,
      addressCountry: airport.country_code,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: airport.lat,
      longitude: airport.lng,
    },
    url: `https://flynout.com/airport/${airport.iata_code}`,
  };
  return <SEOHead title={title} description={description} image={airport.image_url} url={`/airport/${airport.iata_code}`} keywords={keywords} structuredData={structuredData} />;
}

// Helper for flight pages
export function FlightSEO({ flight, roomId }) {
  if (!flight) {
    const flightNum = roomId?.replace('flight_', '');
    return <SEOHead title={`Flight ${flightNum} Community`} description={`Join the Fly N Out community for flight ${flightNum}. Share gate updates, delays, seat tips, and connect with passengers on your flight in real time.`} url={`/flight/${roomId}`} />;
  }
  const title = `Flight ${flight.flight_number}: ${flight.origin_code} → ${flight.destination_code}`;
  const description = `Live community for ${flight.airline} flight ${flight.flight_number} from ${flight.origin} to ${flight.destination}. Get gate updates, share seat tips, and connect with fellow passengers.`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Flight',
    flightNumber: flight.flight_number,
    provider: { '@type': 'Airline', name: flight.airline, iataCode: flight.airline_code },
    departureAirport: { '@type': 'Airport', iataCode: flight.origin_code, name: flight.origin },
    arrivalAirport: { '@type': 'Airport', iataCode: flight.destination_code, name: flight.destination },
  };
  return <SEOHead title={title} description={description} url={`/flight/flight_${flight.flight_number}`} keywords={`${flight.flight_number}, ${flight.airline}, ${flight.origin_code} ${flight.destination_code}, flight community, flight chat`} structuredData={structuredData} />;
}

// Helper for route community pages
export function RouteSEO({ origin, destination, routeId, postCount = 0 }) {
  const routeStr = origin && destination ? `${origin} → ${destination}` : routeId;
  const title = `${routeStr} Route Community — Reviews, Seat Tips & Experiences`;
  const description = `Real traveler reviews, seat recommendations, and flight experiences for the ${routeStr} route. ${postCount > 0 ? `${postCount} community posts shared.` : 'Be the first to share your experience.'} Discover the best seats and tips.`;
  const keywords = `${origin || ''} ${destination || ''} flight route, ${routeStr} seat tips, ${routeStr} experiences, ${origin || ''} ${destination || ''} review, flight community`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    headline: title,
    description,
    url: `https://flynout.com/route/${routeId}`,
    keywords,
    author: { '@type': 'Organization', name: 'Fly N Out Community' },
    publisher: { '@type': 'Organization', name: 'Fly N Out', logo: { '@type': 'ImageObject', url: 'https://flynout.com/logo192.png' } },
  };
  return (
    <SEOHead
      title={title}
      description={description}
      url={`/route/${routeId}`}
      keywords={keywords}
      structuredData={structuredData}
    />
  );
}

// Helper for blog posts
export function BlogSEO({ post }) {
  if (!post) return null;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { '@type': 'Person', name: post.author_name },
    publisher: { '@type': 'Organization', name: 'Fly N Out', logo: { '@type': 'ImageObject', url: 'https://flynout.com/logo192.png' } },
    datePublished: post.created_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://flynout.com/blog/${post.slug}` },
  };
  return <SEOHead title={post.title} description={post.excerpt} image={post.cover_image} url={`/blog/${post.slug}`} type="article" keywords={(post.tags || []).join(', ')} structuredData={structuredData} />;
}
